/**
 * React Query keys for source-ui.
 *
 * invalidateQueries vs refetch: Use invalidateQueries when data is updated from
 * another component or callback (e.g. after mutations, campaign refetch). Use
 * refetch when refreshing only in the same component (e.g. "Refresh" or "on open").
 *
 * isLoading vs isFetching: Use isLoading for initial load states (skeletons,
 * full-page loaders, disabled inputs) so the UI only shows loading when there
 * is no cached data. Use isFetching for background refresh indicators (e.g.
 * "loading more" in pagination) so you don't flash skeletons on every refetch.
 */
export const queryKeys = {
  auth: {
    whoami: ['auth', 'whoami'],
  },
  wx: {
    canAskCue: ['wx', 'canAskCue'],
  },
  dashboard: {
    openings: ['dashboard', 'openings'],
    snapshots: ['dashboard', 'snapshots'],
    analytics: ['dashboard', 'analytics'],
    hiringOpenings: ['dashboard', 'hiringOpenings'],
    aggregateSpend: ['dashboard', 'aggregateSpend'],
    performanceStatusMetrics: ['dashboard', 'performanceStatusMetrics'],
    recommendations: {
      sourcing: ['dashboard', 'recommendations', 'sourcing'],
      automation: ['dashboard', 'recommendations', 'automation'],
      atRiskCount: ['dashboard', 'recommendations', 'atRiskCount'],
      atRiskOpenings: ['dashboard', 'recommendations', 'atRiskOpenings'],
    },
  },
  allOpenings: ['allOpenings'],
  allCampaigns: ['allCampaigns'],
  campaignsMetrics: ['campaignsMetrics'],
  filters: {
    channels: ['filters', 'channels'],
    positions: ['filters', 'positions'],
    locations: ['filters', 'locations'],
    brands: ['filters', 'brands'],
  },
  openingDetails: {
    detail: ['openingDetails', 'detail'],
    // Nested under `detail` so every invalidation of the details query also
    // refetches the metrics split out of it.
    analytics: ['openingDetails', 'detail', 'analytics'],
    hiringGoals: ['openingDetails', 'hiringGoals'],
    hiringOpenings: ['openingDetails', 'hiringOpenings'],
    channels: {
      list: ['openingDetails', 'channels', 'list'],
      sourcingFunnels: ['openingDetails', 'channels', 'sourcingFunnels'],
      campaignDetails: ['openingDetails', 'channels', 'campaignDetails'],
      generalRecommendation: [
        'openingDetails',
        'channels',
        'generalRecommendation',
      ],
      budgetRecommendation: [
        'openingDetails',
        'channels',
        'budgetRecommendation',
      ],
      recommendations: ['openingDetails', 'channels', 'recommendations'],
      reach: ['openingDetails', 'channels', 'reach'],
    },
    funnel: {
      details: ['openingDetails', 'funnel', 'details'],
      hireStages: ['openingDetails', 'funnel', 'hireStages'],
    },
  },
  settings: {
    automations: ['settings', 'automations'],
    channels: {
      nonIntegrated: ['settings', 'channels', 'nonIntegrated'],
      utmMapping: ['settings', 'channels', 'utmMapping'],
      integrated: ['settings', 'channels', 'integrated'],
    },
    contracts: {
      vonqContracts: ['settings', 'contracts', 'vonqContracts'],
      vonqChannels: ['settings', 'contracts', 'vonqChannels'],
      vonqChannelDetails: ['settings', 'contracts', 'vonqChannelDetails'],
    },
  },
  jobPosting: {
    selectOpenings: ['jobPosting', 'selectOpenings'],
    channels: {
      popular: ['jobPosting', 'channels', 'popular'],
      free: ['jobPosting', 'channels', 'free'],
      paid: ['jobPosting', 'channels', 'paid'],
    },
    jobDetails: ['jobPosting', 'jobDetails'],
    postingRequirements: ['jobPosting', 'postingRequirements'],
    audienceOptions: ['jobPosting', 'audienceOptions'],
    poolConflicts: ['jobPosting', 'poolConflicts'],
    vonqAutocomplete: {
      lazy: (contractId: string, fieldName: string) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          'lazy',
        ] as const,
      search: (contractId: string, fieldName: string, term: string) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          term,
        ] as const,
      dependent: (
        contractId: string,
        fieldName: string,
        term: string | string[],
      ) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          'dependent',
          JSON.stringify(term),
        ] as const,
    },
  },
} as const;

/**
 * Queries that depend on campaign create/update/status. Cue tool invalidation
 * and in-app campaign mutations share this list so they cannot drift.
 */
export const campaignDependentQueryKeys = [
  queryKeys.openingDetails.detail,
  queryKeys.openingDetails.channels.list,
  queryKeys.openingDetails.channels.sourcingFunnels,
  queryKeys.openingDetails.channels.campaignDetails,
  queryKeys.openingDetails.channels.recommendations,
  queryKeys.openingDetails.channels.reach,
  queryKeys.allCampaigns,
  queryKeys.campaignsMetrics,
  queryKeys.dashboard.openings,
  queryKeys.dashboard.analytics,
  queryKeys.dashboard.snapshots,
] as const;
